import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifyActiveEventsUpdated } from '../../../../utils/activeEventsBus';
import axios from 'axios';
import { Box, TextField, MenuItem, Alert, FormLabel, Typography } from '@mui/material';
import {
  FormShell,
  FormActions,
  SectionLabel,
  FieldLabel,
  EmptyOption,
  useLocationPicker,
  inputSx,
} from '../FormUI/RoadAccidentFormUI';
import useMunicipalityCascade from '../FormUI/useMunicipalityCascade';
import MunicipalityFields from '../FormUI/MunicipalityFields';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Submitting a report now records who submitted it, so it requires a
// logged-in session - the same JWT every other authenticated call in this
// app already attaches.
const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const vehicleOptions = [
  { value: 'bike', label: 'Bike' },
  { value: 'bus', label: 'Bus' },
  { value: 'car', label: 'Car' },
  { value: 'van', label: 'Van' },
  { value: 'lorry', label: 'Lorry' },
  { value: 'heavyVehicle', label: 'Heavyload/Heavy Vehicle' },
];

// Asia/Dili is a fixed UTC+9 offset year-round (no daylight saving), so the
// current Timor time can be computed directly instead of needing a timezone
// library - shift the epoch by +9h, then read it back with the UTC getters
// so the browser's own local timezone never enters the calculation.
const getTimorDateTimeLocal = () => {
  const DILI_OFFSET_MS = 9 * 60 * 60 * 1000;
  const diliNow = new Date(Date.now() + DILI_OFFSET_MS);
  const pad = (n) => String(n).padStart(2, '0');
  return `${diliNow.getUTCFullYear()}-${pad(diliNow.getUTCMonth() + 1)}-${pad(diliNow.getUTCDate())}T${pad(diliNow.getUTCHours())}:${pad(diliNow.getUTCMinutes())}`;
};

// Native <input type="datetime-local"> only opens its calendar popup when
// the small browser-drawn icon itself is clicked - this makes a click
// anywhere in the field do the same, via the (Chrome/Edge) showPicker()
// API. No-ops quietly on browsers that don't support it (Safari/Firefox).
const openDateTimePicker = (event) => {
  if (typeof event.target.showPicker === 'function') {
    try { event.target.showPicker(); } catch (error) { /* ignore - e.g. not user-initiated */ }
  }
};

const initialFormData = {
  accident_type: '',
  motorizedvehicle1: '',
  motorizedvehicle2: '',
  motorized_vehicle: '',
  others: '',
  location: '',
  latitude: '',
  longitude: '',
  datetime: getTimorDateTimeLocal(),
  message: '',
  people_injured_count: '',
  people_deaths_count: '',
  houses_affected_count: '',
  livestock_lost_count: '',
  estimated_loss_amount: '',
};

function Reportaccident() {
  const navigate = useNavigate();
  const cascade = useMunicipalityCascade();
  const [formData, setFormData] = useState(initialFormData);
  const [image, setImage] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (latitude, longitude) => {
    setFormData((prev) => ({ ...prev, latitude, longitude }));
  };

  const handleLocationResolved = (adm) => {
    if (adm) cascade.applyResolvedAdm(adm.adm1_code, adm.adm2_code, adm.adm3_code);
  };

  const handleLandmark = (landmark) => {
    setFormData((prev) => ({ ...prev, location: landmark || '' }));
  };

  const { latLngFields, mapElement, locationError, landmarkNotFound } = useLocationPicker({
    latitude: formData.latitude,
    longitude: formData.longitude,
    onChange: handleLocationChange,
    onLocationResolved: handleLocationResolved,
    onLandmark: handleLandmark,
    infoText: 'Click on the map to mark the accident location for your report.',
  });

  const handleReset = () => {
    setFormData(initialFormData);
    setImage(null);
    cascade.reset();
    setSubmitSuccess(false);
    setSubmitError('');
  };

  const handleBackClick = () => {
    navigate('/Roadaccident/Reports?tab=roadaccident');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);

    if (!formData.message || !formData.message.trim()) {
      setSubmitError('Description is required.');
      return;
    }

    const dataToSend = new FormData();
    dataToSend.append('municipality', cascade.selectedMunicipality);
    dataToSend.append('submunicipality', cascade.selectedSubMunicipality);
    dataToSend.append('suco', cascade.selectedSuco);
    dataToSend.append('accident_type', formData.accident_type);

    if (formData.accident_type === 'motorizedAccident') {
      dataToSend.append('motorized_vehicle1', formData.motorizedvehicle1 || '');
      dataToSend.append('motorized_vehicle2', formData.motorizedvehicle2 || '');
    } else if (formData.accident_type === 'motorizedToOthers') {
      dataToSend.append('motorized_vehicle', formData.motorized_vehicle || '');
      dataToSend.append('others', formData.others || '');
    }

    dataToSend.append('location', formData.location);
    dataToSend.append('latitude', formData.latitude);
    dataToSend.append('longitude', formData.longitude);
    dataToSend.append('datetime', formData.datetime);
    dataToSend.append('message', formData.message);
    if (formData.people_injured_count !== '') dataToSend.append('people_injured_count', formData.people_injured_count);
    if (formData.people_deaths_count !== '') dataToSend.append('people_deaths_count', formData.people_deaths_count);
    if (formData.houses_affected_count !== '') dataToSend.append('houses_affected_count', formData.houses_affected_count);
    if (formData.livestock_lost_count !== '') dataToSend.append('livestock_lost_count', formData.livestock_lost_count);
    if (formData.estimated_loss_amount !== '') dataToSend.append('estimated_loss_amount', formData.estimated_loss_amount);
    if (image) {
      dataToSend.append('image', image);
    }

    try {
      const response = await axios.post(
        `${backendUrl}roadaccident/submit_report_road_accident/`,
        dataToSend,
        { headers: { 'Content-Type': 'multipart/form-data', ...authHeaders() } }
      );
      if (response.status === 200 || response.status === 201) {
        setSubmitSuccess(true);
        notifyActiveEventsUpdated();
        setTimeout(handleReset, 2000);
      } else {
        setSubmitError('Failed to submit report. Please try again.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || JSON.stringify(error.response?.data) || error.message;
      setSubmitError(`An error occurred while submitting the report: ${errorMessage}`);
    }
  };

  return (
    <FormShell title="Report Road Accident" subtitle="Submit a road accident field report" onBack={handleBackClick} onSubmit={handleSubmit}>
      {submitSuccess && <Alert severity="success" sx={{ fontSize: '14px', borderRadius: '8px' }}>Road Accident Report submitted successfully.</Alert>}
      {submitError && <Alert severity="error" sx={{ fontSize: '14px', borderRadius: '8px' }}>{submitError}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '60% 40%' }, gap: 3 }}>
        {/* LEFT: Location Information */}
        <Box>
          <SectionLabel text="Location Information" />

          {locationError && !(cascade.selectedMunicipality && cascade.selectedSubMunicipality && cascade.selectedSuco) && (
            <Alert severity="warning" sx={{ fontSize: '13px', borderRadius: '8px', mb: 2 }}>{locationError}</Alert>
          )}

          <MunicipalityFields cascade={cascade} />
          <Box sx={{ mt: 2.5 }}>
            <TextField
              label="Location"
              size="small"
              fullWidth
              sx={inputSx}
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter location details"
            />
            {landmarkNotFound && !formData.location && (
              <Typography sx={{ fontSize: '12px', color: '#b45309', mt: 0.5 }}>No landmark found nearby — add one.</Typography>
            )}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            {latLngFields}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            <TextField
              label="Date and Time"
              type="datetime-local"
              size="small"
              fullWidth
              sx={inputSx}
              name="datetime"
              value={formData.datetime}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ onClick: openDateTimePicker, style: { cursor: 'pointer' } }}
            />
          </Box>
        </Box>

        {/* RIGHT: Map */}
        <Box>{mapElement}</Box>
      </Box>

      <Box>
        <SectionLabel text="Accident Details" />
        <TextField
          label="Type of Accident"
          select
          size="small"
          fullWidth
          sx={inputSx}
          name="accident_type"
          value={formData.accident_type}
          onChange={handleChange}
        >
          <EmptyOption text="Select accident type" />
          <MenuItem value="motorizedAccident">Motorized Accident</MenuItem>
          <MenuItem value="motorizedToOthers">Motorized to Others</MenuItem>
        </TextField>

        {formData.accident_type === 'motorizedAccident' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
            <TextField
              label="Motorized Vehicle 1"
              select
              size="small"
              fullWidth
              sx={inputSx}
              name="motorizedvehicle1"
              value={formData.motorizedvehicle1}
              onChange={handleChange}
            >
              <EmptyOption text="Select a vehicle" />
              {vehicleOptions.map((v) => (
                <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Motorized Vehicle 2"
              select
              size="small"
              fullWidth
              sx={inputSx}
              name="motorizedvehicle2"
              value={formData.motorizedvehicle2}
              onChange={handleChange}
            >
              <EmptyOption text="Select a vehicle" />
              {vehicleOptions.map((v) => (
                <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {formData.accident_type === 'motorizedToOthers' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5, mt: 2.5 }}>
            <TextField
              label="Motorized Vehicle"
              select
              size="small"
              fullWidth
              sx={inputSx}
              name="motorized_vehicle"
              value={formData.motorized_vehicle}
              onChange={handleChange}
            >
              <EmptyOption text="Select a vehicle" />
              {vehicleOptions.map((v) => (
                <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Others"
              size="small"
              fullWidth
              sx={inputSx}
              name="others"
              value={formData.others}
              onChange={handleChange}
              placeholder="Specify other details"
            />
          </Box>
        )}
      </Box>

      <Box>
        <SectionLabel text="Losses & Damages" />
        <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1.5 }}>
          Approximate figures are fine — leave blank if unknown.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' }, gap: 2.5 }}>
          <TextField
            label="People injured (approx.)"
            type="number"
            size="small"
            fullWidth
            sx={inputSx}
            name="people_injured_count"
            value={formData.people_injured_count}
            onChange={handleChange}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Deaths reported (approx.)"
            type="number"
            size="small"
            fullWidth
            sx={inputSx}
            name="people_deaths_count"
            value={formData.people_deaths_count}
            onChange={handleChange}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Vehicles/structures affected (approx.)"
            type="number"
            size="small"
            fullWidth
            sx={inputSx}
            name="houses_affected_count"
            value={formData.houses_affected_count}
            onChange={handleChange}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Livestock lost (approx.)"
            type="number"
            size="small"
            fullWidth
            sx={inputSx}
            name="livestock_lost_count"
            value={formData.livestock_lost_count}
            onChange={handleChange}
            inputProps={{ min: 0 }}
          />
          <TextField
            label="Estimated monetary loss (USD)"
            type="number"
            size="small"
            fullWidth
            sx={inputSx}
            name="estimated_loss_amount"
            value={formData.estimated_loss_amount}
            onChange={handleChange}
            inputProps={{ min: 0, step: '0.01' }}
          />
        </Box>
      </Box>

      <Box>
        <SectionLabel text="Description" />
        <FieldLabel text="Describe what happened" required />
        <TextField
          multiline
          rows={4}
          fullWidth
          required
          sx={inputSx}
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Provide a description of this report"
        />
        <Box sx={{ mt: 2.5 }}>
          <FieldLabel text="Image" />
          <FormLabel
            component="label"
            sx={{
              display: 'inline-block', fontSize: '14px', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: '8px', px: 2, py: 1, cursor: 'pointer',
              '&:hover': { borderColor: '#2563eb' },
            }}
          >
            {image ? image.name : 'Choose file'}
            <input hidden type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} />
          </FormLabel>
        </Box>
      </Box>

      <FormActions onReset={handleReset} submitLabel="Submit" />
    </FormShell>
  );
}

export default Reportaccident;
